import {api} from "../config/api";
import secureStorage from "./secureStorage";

/**
 * Génère un code-barres EAN-13 valide (12 chiffres aléatoires + 1 chiffre de contrôle).
 *
 * @param {string} [prefix=""] - Préfixe optionnel (ex. préfixe pays/entreprise) utilisé
 *   comme début des 12 premiers chiffres. Les chiffres non numériques sont ignorés.
 * @returns {string} Un code EAN-13 de 13 chiffres.
 */
export function generateEAN13(prefix = "") {
    // On ne garde que les chiffres du préfixe et on tronque à 12 chiffres max.
    const base = String(prefix).replace(/\D/g, "").slice(0, 12);

    // Complète avec des chiffres aléatoires pour atteindre 12 chiffres.
    let digits = base;
    while (digits.length < 12) {
        digits += Math.floor(Math.random() * 10).toString();
    }

    return digits + computeEAN13CheckDigit(digits);
}

/**
 * Calcule le chiffre de contrôle EAN-13 à partir des 12 premiers chiffres.
 *
 * @param {string} twelveDigits - Chaîne de 12 chiffres.
 * @returns {string} Le chiffre de contrôle (0-9).
 */
export function computeEAN13CheckDigit(twelveDigits) {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const digit = Number(twelveDigits[i]);
        // Poids 1 pour les positions impaires, 3 pour les positions paires (1-indexé).
        sum += i % 2 === 0 ? digit : digit * 3;
    }
    return String((10 - (sum % 10)) % 10);
}

/**
 * Génère un code-barres pour un produit via l'API backend.
 * L'API vérifie l'unicité et crée le code-barres.
 *
 * @param {string} [barcode] - Code-barres optionnel à vérifier/créer.
 * @returns {Promise<string>} Le code-barres généré par le backend.
 */
export async function generateProductBarcode(barcode) {
    const selectedAccount = await secureStorage.getItem("selectedAccount");
    const accountId = selectedAccount ? selectedAccount.id : null;

    const response = await api.post(
        "/products/barcode/check-and-create",
        barcode ? { barcode } : {},
        {
            headers: {
                "X-account-id": accountId,
            },
        }
    );
    return response.data.barcode;
}

/**
 * Génère un code-barres pour une variante via l'API backend.
 * L'API vérifie l'unicité et crée le code-barres.
 *
 * @returns {Promise<string>} Le code-barres généré par le backend.
 */
export async function generateVariantBarcode() {
    const selectedAccount = await secureStorage.getItem("selectedAccount");
    const accountId = selectedAccount ? selectedAccount.id : null;

    const response = await api.post(
        "/variants/barcode/check-and-create",
        {},
        {
            headers: {
                "X-account-id": accountId,
            },
        }
    );
    return response.data.barcode;
}
